import React from 'react';
import * as boardsStore from '../../extensions/bundled/task-manager/boardsStore';
import { progressPercent } from '../../extensions/bundled/task-manager/boardsOverview';
import BoardTileMenu from './BoardTileMenu';
import { BoardScopeChip, BoardLocationChip, BoardProgress, BoardOverdueChip, BoardLabelDots, BoardOwnerAvatar, BoardPinStar } from './BoardTileParts';
import Tooltip from '../ui/Tooltip';
import './BoardOverviewTile.css';
import './BoardOverviewTileFull.css';
import './BoardOverviewTileNeutral.css';
import { isNeutralBoardColor } from './boardColor';
import { boardBackground } from '../../extensions/bundled/task-manager/boardColorValue';

export { DEFAULT_BOARD_GRADIENT } from '../../extensions/bundled/task-manager/boardColorValue';

/**
 * One board on the Boards overview (cards 7.1 + 7.4). FULL-COLOUR tile
 * (owner, 13.10.2026, artboard BoardsGalleryFull): the whole tile carries the
 * board's gradient (--tile-grad, BoardOverviewTileFull.css), then the name with the pin
 * star and the ⋯ menu (BoardTileMenu), the description, the Personal / team
 * and Cloud / This device chips, and below a divider the stats strip —
 * progress, overdue, label dots, last opened here and the owner's avatar.
 *
 * Every board action (share, move, pin, edit, delete) lives in the ⋯ menu.
 * The star only shows that a board is pinned. The owner (extras §2.8) is
 * resolved by the caller (useBoardOwners) and left out when unknown. The
 * whole tile opens the board; a real button spans it so the keyboard reaches
 * it too.
 */
export default function BoardTile(props) {
  const { board, teamLabel, onOpen, openedLabel, owner, pinned = false } = props;
  const isTeam = board.visibility === 'team';
  // Team boards are always in the cloud.
  const onDevice = !isTeam && board.location === boardsStore.LOCATION_DEVICE;
  const total = board.taskCount || 0;
  const done = board.doneCount || 0;
  return (
    <div
      className={`boards-tile boards-tile--full${isNeutralBoardColor(board.color) ? ' boards-tile--neutral' : ''}`}
      data-board-id={board.id}
      style={{ '--tile-grad': boardBackground(board.color) }}
      onClick={() => onOpen(board)}
    >
      <button
        type="button"
        className="boards-tile-open"
        aria-label={`Open board ${board.name}`}
        onClick={(e) => {
          e.stopPropagation();
          onOpen(board);
        }}
      />
      <div className="boards-tile-head">
        <div className="boards-tile-head-row">
          <span className="boards-tile-name">{board.name}</span>
          <div className="boards-tile-badges">
            <BoardPinStar pinned={pinned} />
            <BoardTileMenu {...props} onDevice={onDevice} />
          </div>
        </div>
        {board.description ? <p className="boards-tile-desc">{board.description}</p> : null}
        <div className="boards-tile-spacer" />
        <div className="boards-tile-chips">
          <BoardScopeChip isTeam={isTeam} teamLabel={teamLabel} />
          <BoardLocationChip onDevice={onDevice} />
        </div>
      </div>
      <div className="boards-tile-stats">
        <BoardProgress done={done} total={total} percent={progressPercent(board)} />
        <div className="boards-tile-meta-row">
          <div className="boards-tile-meta">
            <BoardOverdueChip count={board.overdueCount || 0} />
            <BoardLabelDots colors={board.labelColors} />
            {openedLabel ? (
              <Tooltip content="Last opened on this device" side="bottom">
                <span className="boards-tile-opened">{openedLabel}</span>
              </Tooltip>
            ) : null}
          </div>
          <BoardOwnerAvatar owner={owner} />
        </div>
      </div>
    </div>
  );
}
